import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Banknote,
  Search,
  RefreshCw,
  History,
  Plus,
  X,
  DollarSign,
} from 'lucide-react';
import DataTable from '../../components/shared/DataTable';
import { useToastContext } from '../../contexts/ToastContext';
import {
  getAdminVendorPayoutVendors,
  getAdminVendorDisbursements,
  postAdminVendorDisbursement,
} from '../../services/vendorPayoutService';

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
};

const AdminVendorPayouts = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();

  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    per_page: 25,
    sort_by: 'business_name',
    sort_order: 'asc',
  });

  const [historyVendor, setHistoryVendor] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [payVendor, setPayVendor] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: '',
    notes: '',
    booking_id: '',
    currency: 'USD',
  });

  const queryParams = useMemo(
    () => ({
      page: filters.page,
      per_page: filters.per_page,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      due_only: true,
      ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    }),
    [filters]
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-vendor-payout-vendors', queryParams],
    queryFn: async () => getAdminVendorPayoutVendors(queryParams),
    placeholderData: (prev) => prev,
  });

  const pagination = data?.data;
  const rows = pagination?.data ?? [];

  const historyQuery = useQuery({
    queryKey: ['admin-vendor-disbursements', historyVendor?.id, historyPage],
    queryFn: async () =>
      getAdminVendorDisbursements(historyVendor.id, { page: historyPage, per_page: 20 }),
    enabled: Boolean(historyVendor?.id),
    placeholderData: (prev) => prev,
  });

  const recordMutation = useMutation({
    mutationFn: async () => {
      const amount = parseFloat(String(payForm.amount).replace(/,/g, ''), 10);
      if (Number.isNaN(amount) || amount <= 0) {
        throw new Error('Enter a valid amount greater than zero.');
      }
      const payload = {
        amount,
        currency: (payForm.currency || 'USD').trim().toUpperCase() || 'USD',
        notes: payForm.notes?.trim() || undefined,
        booking_id: payForm.booking_id?.trim() || undefined,
      };
      return postAdminVendorDisbursement(payVendor.id, payload);
    },
    onSuccess: (res) => {
      success(res?.message || 'Disbursement recorded.');
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
      if (historyVendor?.id === payVendor?.id) {
        queryClient.invalidateQueries({ queryKey: ['admin-vendor-disbursements', payVendor.id] });
      }
      setPayVendor(null);
      setPayForm({ amount: '', notes: '', booking_id: '', currency: 'USD' });
    },
    onError: (err) => {
      showError(err?.message || 'Could not record disbursement.');
    },
  });

  const openPay = (row) => {
    const avail = row.ledger?.available_balance ?? 0;
    setPayVendor(row);
    setPayForm({
      amount: avail > 0 ? String(avail.toFixed(2)) : '',
      notes: '',
      booking_id: '',
      currency: 'USD',
    });
  };

  const columns = [
    {
      key: 'vendor',
      label: 'Vendor',
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{row.business_name || '—'}</p>
          <p className="text-xs text-gray-500 truncate">{row.user?.email}</p>
          <Link
            to={`/admin/vendors/${row.user_id || row.user?.id}`}
            className="text-xs text-primary-600 hover:underline font-medium"
          >
            Vendor profile
          </Link>
        </div>
      ),
    },
    {
      key: 'earned',
      label: 'Earned (orders)',
      render: (row) => (
        <span className="text-sm font-semibold text-gray-900">{fmtMoney(row.ledger?.total_earned_from_orders)}</span>
      ),
    },
    {
      key: 'disbursed',
      label: 'Sent to vendor',
      render: (row) => (
        <span className="text-sm text-gray-800">{fmtMoney(row.ledger?.total_disbursed)}</span>
      ),
    },
    {
      key: 'available',
      label: 'Due to pay',
      render: (row) => (
        <span className="text-sm font-semibold text-emerald-800">{fmtMoney(row.ledger?.available_balance)}</span>
      ),
    },
    {
      key: 'remaining',
      label: 'Ledger',
      render: (row) => (
        <span
          className={`text-sm font-medium ${
            (row.ledger?.remaining_after_disbursements ?? 0) < 0 ? 'text-red-700' : 'text-gray-700'
          }`}
          title="Earned minus all recorded payouts (can be negative if over-paid)"
        >
          {fmtMoney(row.ledger?.remaining_after_disbursements)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => {
              setHistoryPage(1);
              setHistoryVendor(row);
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <History className="w-3.5 h-3.5" />
            History
          </button>
          <button
            type="button"
            onClick={() => openPay(row)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-black text-white text-xs font-medium hover:bg-gray-800"
          >
            <Plus className="w-3.5 h-3.5" />
            Record payout
          </button>
        </div>
      ),
    },
  ];

  const histPag = historyQuery.data?.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Banknote className="w-7 h-7 text-primary-600" />
            Vendor order payouts
          </h1>
          <p className="text-gray-600 mt-1 text-sm max-w-2xl">
            Vendors with order earnings waiting to be sent out. Only vendors with a balance due to pay are listed.
            Record amounts you send outside the app; vendors see these entries on their payout history page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="search"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
                placeholder="Business name, vendor email…"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-black"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort</label>
            <select
              value={`${filters.sort_by}:${filters.sort_order}`}
              onChange={(e) => {
                const [sort_by, sort_order] = e.target.value.split(':');
                setFilters((f) => ({ ...f, sort_by, sort_order, page: 1 }));
              }}
              className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="business_name:asc">Name A–Z</option>
              <option value="business_name:desc">Name Z–A</option>
              <option value="created_at:desc">Newest profile</option>
              <option value="created_at:asc">Oldest profile</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
            {error.message || 'Failed to load vendors.'}
          </div>
        )}

        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading && !data}
          searchable={false}
          filterable={false}
          sortable={false}
          showPagination={false}
          emptyMessage="No vendors currently have a payout due."
        />

        {pagination && pagination.last_page > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Page {pagination.current_page} of {pagination.last_page}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.current_page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {historyVendor && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40" role="dialog">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payout history</h2>
                <p className="text-sm text-gray-600 mt-0.5">{historyVendor.business_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryVendor(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {historyQuery.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
              {historyQuery.error && (
                <p className="text-sm text-red-600">{historyQuery.error.message || 'Failed to load history.'}</p>
              )}
              {(histPag?.data ?? []).length === 0 && !historyQuery.isLoading && (
                <p className="text-sm text-gray-500">No disbursements recorded yet.</p>
              )}
              {(histPag?.data ?? []).map((d) => (
                <div key={d.id} className="rounded-lg border border-gray-100 p-3 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-gray-900">{fmtMoney(d.amount)}</span>
                    <span className="text-xs text-gray-500">{formatDt(d.created_at)}</span>
                  </div>
                  <p className="text-xs font-mono text-gray-600 mt-1">{d.disbursement_reference}</p>
                  {d.booking?.booking_reference && (
                    <p className="text-xs text-gray-600">Booking: {d.booking.booking_reference}</p>
                  )}
                  {d.notes && <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{d.notes}</p>}
                  {d.recorded_by && (
                    <p className="text-xs text-gray-500 mt-1">Recorded by {d.recorded_by.name || d.recorded_by.email}</p>
                  )}
                </div>
              ))}
            </div>
            {histPag && histPag.last_page > 1 && (
              <div className="p-3 border-t border-gray-100 flex justify-between items-center">
                <button
                  type="button"
                  disabled={historyPage <= 1}
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  className="text-sm px-3 py-1.5 rounded border disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-600">
                  {historyPage} / {histPag.last_page}
                </span>
                <button
                  type="button"
                  disabled={historyPage >= histPag.last_page}
                  onClick={() => setHistoryPage((p) => p + 1)}
                  className="text-sm px-3 py-1.5 rounded border disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {payVendor && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40" role="dialog">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary-600" />
                  Record payout
                </h2>
                <p className="text-sm text-gray-600 mt-1">{payVendor.business_name}</p>
                <p className="text-xs text-gray-500">
                  Available (for reference): {fmtMoney(payVendor.ledger?.available_balance)} — you may record any
                  positive amount.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !recordMutation.isPending && setPayVendor(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="text"
                inputMode="decimal"
                value={payForm.amount}
                onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency (optional)</label>
              <input
                type="text"
                maxLength={3}
                value={payForm.currency}
                onChange={(e) => setPayForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase"
                placeholder="USD"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={payForm.notes}
                onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Check #, ACH ref, period covered…"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID (optional)</label>
              <input
                type="text"
                value={payForm.booking_id}
                onChange={(e) => setPayForm((f) => ({ ...f, booking_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="UUID if tying to one booking"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setPayVendor(null)}
                disabled={recordMutation.isPending}
                className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => recordMutation.mutate()}
                disabled={recordMutation.isPending}
                className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {recordMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function formatDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default AdminVendorPayouts;
